import { useState, useRef, useCallback, useEffect } from "react";
import AMSListing from "../../../components/listing/AMSListing";
import { useToast } from "../../../components/toast/ToastContext";
import DeletePrompt from "../../../components/DeletePrompt";
import { useNavigate } from "react-router-dom";
import {
  useDeleteCustomFormMutation,
  useDuplicateCustomFormMutation,
  useGetCustomFormByIdQuery,
  useGetCustomFormsQuery,
  useGetScopesforFieldsQuery,
  useLazyGetCustomFormByIdQuery,
} from "../../../api/formsApiSlice";
import ShareFormModal from "./ShareFormModal";
import { ActionType, Column, Form, Scope } from "../../../types/types";
import FormPreviewModal from "../../../components/FormBuilder/FormPreview/FormPreviewModal";
import { set } from "lodash";

interface CustomFormsListingProps {
  onEditForm: (id: number, onSuccess: () => void) => void;
  refreshTrigger?: number;
}

const CustomFormsListing = ({
  onEditForm,
  refreshTrigger,
}: CustomFormsListingProps) => {
  const staticColumns: Column[] = [
    { id: "id", label: "Id", isSortable: true },
    { id: "name", label: "Name", isSortable: true },
    { id: "scope", label: "Scope", isSortable: false, },
    { id: 'description', label: "Description", isSortable: false },
    { id: "isStandard", label: "Category", isFlag: true },
    { id: "statusId", label: "Status", isFlag: true },
    { id: "account", label: "Created By", isSortable: false },
    { id: "createdAt", label: "Creation Date", isSortable: true },
  ];
  const { data: scopesData = [], isLoading: scopesLoading } = useGetScopesforFieldsQuery({ isVisible: true });

  const showToast = useToast();
  const navigate = useNavigate();
  const [showDeletePrompt, setShowDeletePrompt] = useState(false);
  const filterGroups = [
    {
      key: 'createdAt',
      label: 'Creation Date',
      dataType: 'date',
      options: [],
    },
    {
      key: 'scopeId',
      label: 'Scope',
      dataType: 'multi-select',
      options: scopesData?.scopes?.map((scope: Scope) => ({
        value: scope.id,
        label: scope.displayName,
      })) || [],
    },
  ];

  const [shareModal, setShareModal] = useState(false);
  const [selectedRecordIds, setSelectedRecordIds] = useState<number[]>([]);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [showFormPreview, setShowFormPreview] = useState(false);
  const [previewFormId, setPreviewFormId] = useState<number | null>(null);
  const [formData, setFormData] = useState<Form>();
  const [fetchFormById, { data: previewFormData, isLoading: isPreviewLoading }] =
    useLazyGetCustomFormByIdQuery();
  const lastParamsRef = useRef<{
    page: number;
    perPage: number;
    sort?: { field: string; direction: "ASC" | "DESC" };
    query?: string;
    filters: Record<string, any>;
  }>({
    page: 1,
    perPage: 10,
    filters: {},
  });

  const [initialFilters, setInitialFilters] = useState({
    ...lastParamsRef.current,
    query: "",
  });

  const { sort, filters, ...rest } = initialFilters;

  const params = {
    ...rest,
    ...filters,
    sort: sort?.field,
    sortDir: sort?.direction,
  };

  const {
    data: CustomForm,
    refetch,
    isLoading,
    isFetching,
  } = useGetCustomFormsQuery(
    { ...params },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const [deleteCustomForm] = useDeleteCustomFormMutation();
  const [duplicateCustomForm] = useDuplicateCustomFormMutation();

  const confirmDelete = async () => {
    if (!selectedRecordIds.length && !deleteId) return;
    setShowDeletePrompt(false);
    try {
      if (selectedRecordIds.length === 0) {
        selectedRecordIds.push(deleteId!);
      }
      await deleteCustomForm(selectedRecordIds).unwrap();
      refetch();
      showToast(
        "Custom Form Deleted",
        "The selected custom form has been deleted.",
        "success"
      );
    } catch {
      showToast(
        "Delete Failed",
        "Failed to delete custom form. Published form cannot be deleted",
        "error"
      );
    }

    setSelectedRecordIds([]);
  };

  const cancelDelete = () => {
    setShowDeletePrompt(false);
    setSelectedRecordIds([]);
  };
  useEffect(() => {
    if (previewFormId) {

      setFormData(previewFormData);
      setShowFormPreview(true);
    }
  }, [previewFormId, setPreviewFormId]);
  const handleAction = useCallback(
    async (action: ActionType, id: string) => {
      const numericId = Number(id);

      if (action === "delete") {
        setDeleteId(numericId);
        setShowDeletePrompt(true);
        return;
      }

      if (action === "copy") {
        try {
          await duplicateCustomForm(numericId).unwrap();
          refetch();
          showToast(
            "Form Duplicated",
            "The field has been successfully duplicated.",
            "success"
          );
        } catch (error) {
          showToast(
            "Duplication Failed",
            "Failed to duplicate form. Please try again.",
            "error"
          );
        }
        return;
      }

      if (action === "edit") {
        navigate(`${numericId}`);
      }

      if (action === "share") {
        setShareModal(true);
      }

      if (action === "view") {
        try {
          const result = await fetchFormById(numericId);
          if ("data" in result) {
            setFormData(result.data);
            setShowFormPreview(true);
          } else {
            throw new Error();
          }
        } catch {
          showToast(
            "View Failed",
            "Failed to load form data for preview.",
            "error"
          );
        }
      }
    },
    [refetch, onEditForm, navigate]
  );

  return (

    <>
      <AMSListing
        // showActions
        columns={staticColumns}
        filterGroups={filterGroups}
        routeKey="forms"
        noDataTitle="No Form Found"
        noDataSubtitle="Your forms list is currently empty. Start adding new forms to keep everything organized and easy to find."
        title="All Forms"
        subtitle="All custom forms and templates will show here"
        data={CustomForm?.forms.map((form: Form) => ({
          ...form,
          scope: form.scope?.displayName
        })) || []}
        totalItems={CustomForm?.totalCount}
        loading={isLoading || isFetching}
        onAction={handleAction}
        setInitialFilters={setInitialFilters}
        setSelectedRecordIds={setSelectedRecordIds}
        dynamicColumnsListing={false}
        skeletonHeight={'42px'}
        showFilters
      />

      {showDeletePrompt && (
        <DeletePrompt onConfirm={confirmDelete} onCancel={cancelDelete} />
      )}

      {shareModal && (
        <ShareFormModal onConfirm={confirmDelete} onCancel={() => setShareModal(false)} />
      )}

      {showFormPreview && formData && (
        <FormPreviewModal
          pages={formData?.formPages}
          onClose={() => {
            setShowFormPreview(false);
            setFormData(undefined);
          }}
          formTitle={formData?.name ? formData.name : ''}
        />
      )}
    </>
  );
};

export default CustomFormsListing;